/**
 * @fileoverview Edit Client dialog for Active Clients (PATCH /sales/clients/{tenantId}).
 * @description Loads the client's details (with the `tp1.` version tag) and the catalogue choices,
 * then shows EditClientForm filled with the current values. Super Admin only, with a fresh sign-in;
 * the backend decides each refusal (`409 / 4102` registration number in use, `409 / 4126` archived,
 * `412 / 4105` changed since) and it shows as the dialog's error. A client that no longer exists
 * (`404 / 4125`) closes the dialog and reloads the grid. Close is held while the save is in flight,
 * and the confirmation runs from the mutation itself, so it survives the dialog unmounting. The
 * parent mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useClientDetails } from '@/features/sales/hooks/useClientDetails'
import { useClientFormBaseline } from '@/features/sales/hooks/useClientFormBaseline'
import { useEditClient } from '@/features/sales/hooks/useEditClient'
import { useProspectMetadata } from '@/features/sales/hooks/useProspectMetadata'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { editValuesFromClient } from '@/features/sales/lib/sales-clients.utils'
import { ApiException } from '@/shared/api/api.exception'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import EditClientForm from './EditClientForm'
import SalesRequestError from './SalesRequestError'

import type { VersionedClientDetails } from '@/features/sales/types/sales-client.types'

interface EditClientDialogProps {
  tenantId: string
  bankName: string
  onClose: () => void
  onEdited: (bankName: string, result: VersionedClientDetails) => void
  /** Called when the client no longer exists, so the page can reload the grid. */
  onGone: () => void
}

/**
 * @component
 * @param {EditClientDialogProps} props - The client and the handlers.
 * @param {string} props.tenantId - The client to edit.
 * @param {string} props.bankName - The bank's name, shown as the subtitle.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(bankName: string, result: VersionedClientDetails) => void} props.onEdited - Called once
 * the client is saved.
 * @param {() => void} props.onGone - Called when the client no longer exists.
 */
function EditClientDialog({
  tenantId,
  bankName,
  onClose,
  onEdited,
  onGone,
}: EditClientDialogProps) {
  const { t } = useTranslation()
  const details = useClientDetails(tenantId)
  const metadataQuery = useProspectMetadata()
  const refreshSales = useRefreshSalesQueries()
  const edit = useEditClient(
    /**
     * @description Reports the saved edit to the page, even if this dialog has unmounted.
     * @param {VersionedClientDetails} result - The client as saved.
     */
    (result) => onEdited(bankName, result),
  )
  const close = useGuardedClose(onClose, edit.isPending)
  const baseline = useClientFormBaseline(details)
  const { loaded } = baseline
  const startValues = loaded ? editValuesFromClient(loaded.client) : null
  const gone = details.error instanceof ApiException && details.error.status === 404

  useEffect(() => {
    if (gone) onGone()
  }, [gone, onGone])

  /**
   * @description Refreshes the sales lists and loads the latest version over the form's values.
   */
  const refresh = () => {
    refreshSales()
    void baseline.reload()
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="edit-client-dialog"
      title={t('sales.activeClients.edit.title')}
      subtitle={bankName}
      size="lg"
    >
      {loaded && startValues && metadataQuery.isSuccess ? (
        <EditClientForm
          key={loaded.etag}
          client={loaded.client}
          etag={loaded.etag}
          startValues={startValues}
          metadata={metadataQuery.data}
          edit={edit}
          onClose={close}
          onRefresh={refresh}
          isRefreshing={baseline.reloading}
          changedSince={baseline.changedSince}
        />
      ) : loaded && !startValues ? (
        <p role="alert" className="text-compact text-danger" data-testid="edit-client-not-allowed">
          {t('sales.activeClients.edit.notAllowed')}
        </p>
      ) : details.isError || metadataQuery.isError ? (
        <div data-testid="edit-client-load-error">
          {details.isError && !gone ? (
            <SalesRequestError error={details.error} />
          ) : (
            <p role="alert" className="text-compact text-danger">
              {t('sales.activeClients.edit.loadError')}
            </p>
          )}
        </div>
      ) : (
        <p className="text-compact text-slate" data-testid="edit-client-loading">
          {t('sales.activeClients.loading')}
        </p>
      )}
    </Modal>
  )
}

export default EditClientDialog
